import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';
import { isBeforeMinimum } from '@/shared/utils/validationHelpers';

import momentPropTypes from 'react-moment-proptypes';

import intlShape from '@/shared/utils/intlShape';

import dateMessages from '../../messages/dateMessages';
import dateFormatService from '../../services/DateFormatService';

const weekPickerProps = {
  startDateOffset: day => moment(day).startOf('isoWeek'),
  endDateOffset: day => moment(day).endOf('isoWeek'),
};

// Trigger update when calendar closes, not with every change
class DelayedDateRangePicker extends Component {
  constructor(props) {
    super(props);
    const { startDate, endDate } = props;
    this.state = {
      datePickerFocus: null,
      internalStartDate: startDate,
      internalEndDate: endDate,
      renderKey: 0,
    };
  }

  setDates = (startDate, endDate, submitAfterChange = false) => {
    this.setState(
      {
        internalStartDate: startDate,
        internalEndDate: endDate,
      },
      () => {
        if (submitAfterChange) {
          this.submitDates();
        }
      },
    );
  };
  submitDates = () => {
    const { onDatesChange } = this.props;
    const { internalStartDate, internalEndDate } = this.state;
    onDatesChange({ startDate: internalStartDate, endDate: internalEndDate });
  };

  setInputFocus = input => {
    this.setState({ datePickerFocus: input });
  };
  handleDatesChange = ({ startDate, endDate }) => {
    const { internalStartDate, internalEndDate, datePickerFocus } = this.state;
    // This is a check to see if the clear button was pressed

    const submitAfterChange =
      !datePickerFocus &&
      startDate === null &&
      endDate === null &&
      (internalStartDate !== null || internalEndDate !== null);

    this.setDates(startDate, endDate, submitAfterChange);
  };
  handleClose = () => {
    this.setState(
      ({ renderKey }) => ({ renderKey: renderKey + 1 }), // Force rerender, invalid input dates will be reset to null
      () => {
        this.submitDates(); // Submit after setState (including the one in setDates)
      },
    );
  };

  componentDidUpdate(prevProps) {
    const { startDate, endDate } = this.props;
    const { internalStartDate, internalEndDate } = this.state;
    if (
      (prevProps.startDate !== startDate && startDate !== internalStartDate) ||
      (prevProps.endDate !== endDate && endDate !== internalEndDate)
    ) {
      // Dates changed from outside
      this.setDates(startDate, endDate, false);
      // Force rerender
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(({ renderKey }) => ({ renderKey: renderKey + 1 }));
    }
  }

  render() {
    const {
      intl,
      isOutsideRange,
      startDatePlaceholderText,
      endDatePlaceholderText,
      startDateId,
      endDateId,
      startDate,
      endDate,
      onDatesChange,
      isWeekPicker,
      minimumNights = 0,
      iconClassName,
      iconTitle,
      ...rest
    } = this.props;
    const { datePickerFocus, internalStartDate, internalEndDate, renderKey } = this.state;

    const picker = (
      <DateRangePicker
        hideKeyboardShortcutsPanel
        keepFocusOnInput
        displayFormat={dateFormatService.dateFormat()}
        showClearDates
        customArrowIcon="-"
        {...(isWeekPicker ? weekPickerProps : {})}
        {...rest}
        startDate={internalStartDate}
        startDateId={startDateId}
        endDate={internalEndDate}
        endDateId={endDateId}
        onDatesChange={this.handleDatesChange}
        focusedInput={datePickerFocus}
        onFocusChange={this.setInputFocus}
        startDatePlaceholderText={
          startDatePlaceholderText || intl.formatMessage(dateMessages.DateFrom)
        }
        endDatePlaceholderText={
          endDatePlaceholderText || intl.formatMessage(dateMessages.DateUntil)
        }
        isOutsideRange={isOutsideRange}
        onClose={this.handleClose}
        minimumNights={minimumNights}
        key={renderKey}
      />
    );

    if (!iconClassName) return picker;
    return (
      <div className="cx-filter-date-range-with-icon">
        <i
          className={`${iconClassName} cx-filter-date-range-with-icon__icon`}
          title={iconTitle}
          aria-hidden="true"
        />
        {picker}
      </div>
    );
  }

  static propTypes = {
    onDatesChange: PropTypes.func.isRequired,
    intl: intlShape.isRequired,

    startDate: momentPropTypes.momentObj,
    endDate: momentPropTypes.momentObj,
    isOutsideRange: PropTypes.func,
    startDateId: PropTypes.string,
    endDateId: PropTypes.string,
    startDatePlaceholderText: PropTypes.string,
    endDatePlaceholderText: PropTypes.string,
    isWeekPicker: PropTypes.bool,
    iconClassName: PropTypes.string,
    iconTitle: PropTypes.string,
  };
  static defaultProps = {
    startDate: null,
    endDate: null,
    isOutsideRange: isBeforeMinimum,
    startDateId: 'FiltersDateFrom',
    endDateId: 'FiltersDateUntil',
    startDatePlaceholderText: undefined,
    endDatePlaceholderText: undefined,
    isWeekPicker: false,
    iconClassName: undefined,
    iconTitle: undefined,
  };
}

export default injectIntl(DelayedDateRangePicker);
